import { observer } from 'mobx-react';
import { FC, useEffect, useState } from 'react';
import CheckoutProfileApiKey from '../../components/CheckoutProfileApiKey/CheckoutProfileApiKey';
import paymentCheckoutStore from '../../store/paymentCheckoutStore';
import { CheckoutProfile } from '../../types/paymentCheckout';
import { BUTTON_TITLE } from '~/constants/main';
import { localeStore } from '~/mobx/localesStore';
import { TBooleanInNumber } from '~/types/main';
import { booleanToNumber } from '~/utils/helpers';
import Button from '~/view/components/Controls/ButtonComponent/Button';
import Input from '~/view/components/Controls/InputComponent/Input';
import Switcher from '~/view/components/Controls/Switcher/Switcer';
import Text, { TEXT } from '~/view/components/Text/Text';
import IntegrationManager from '~/view/components/IntegrationManager/IntegrationManager';
import integrationsStore from '~/mobx/integrationStore';
import { RESTRICTION_ACCESS } from '~/constants/integrations';
import './style.scss';

const CheckoutProfileSettings: FC = observer((): JSX.Element => {
  const [errors, setErrors] = useState<Map<string, string>>(new Map());

  const { selectedCheckoutProfile } = paymentCheckoutStore;

  useEffect(() => {
    setErrors(new Map());
  }, [selectedCheckoutProfile.id]);

  const isAutoOnlinePaymentDisabled =
    integrationsStore.posRestrictions?.autoAcceptOnlinePaymentToggle === RESTRICTION_ACCESS.DISABLED &&
    selectedCheckoutProfile.branches.some((branch) => Boolean(branch.foodicsId));

  const handleSave = () => {
    const requiredFields = ['secretProdKey', 'publicProdKey'];
    const emptyRequiredFields = requiredFields.filter((field) => !selectedCheckoutProfile[field as keyof CheckoutProfile]);

    if (emptyRequiredFields.length > 0) {
      setErrors(new Map(emptyRequiredFields.map((emptyRequiredField) => [emptyRequiredField, emptyRequiredField])));
    } else {
      paymentCheckoutStore.updateProfile();
      setErrors(new Map());
    }
  };

  const handleSwitcherClick = (field: keyof CheckoutProfile) => (status: TBooleanInNumber) => {
    paymentCheckoutStore.changeSelectedProfileData({ [field]: Boolean(status) });
    setErrors(new Map());
  };

  const handleInputChange = (field: keyof CheckoutProfile) => (value: string | undefined) => {
    paymentCheckoutStore.changeSelectedProfileData({ [field]: value });
    setErrors(new Map());
  };

  return (
    <div className="d-flex flex-column">
      <div className="d-flex align-items-center">
        <Switcher status={booleanToNumber(selectedCheckoutProfile.enabled)} onChange={handleSwitcherClick('enabled')} />
        <Text type={TEXT.SWITCHER_LABEL} text={localeStore.t('enable')} className="family-semibold" />
      </div>

      <div className="d-flex align-items-center mt-6">
        <Switcher status={booleanToNumber(selectedCheckoutProfile.isProductionKey)} onChange={handleSwitcherClick('isProductionKey')} />
        <Text type={TEXT.SWITCHER_LABEL} text={localeStore.t('use_prod_server')} />
      </div>

      <div className="d-flex align-items-center mt-4">
        <IntegrationManager
          setting={integrationsStore.posRestrictions?.autoAcceptOnlinePaymentToggle}
          tooltipOptions={{ show: isAutoOnlinePaymentDisabled }}
          withTooltip
        >
          <Switcher status={booleanToNumber(selectedCheckoutProfile.autoOnlinePayment)} onChange={handleSwitcherClick('autoOnlinePayment')} />
        </IntegrationManager>
        <Text type={TEXT.SWITCHER_LABEL} text={localeStore.t('auto_accept_online_payment')} />
      </div>

      <div className="mt-5">
        <Text type={TEXT.BLOCK_HEADER} text={localeStore.t('sub_channel_name')} />
        <Input
          value={selectedCheckoutProfile.subChannel}
          onChangeValue={handleInputChange('subChannel')}
          className="mt-2"
          contentClassName="checkout-profile-settings__channel-name"
        />
      </div>

      <div className="d-flex flex-row gap-30">
        <div>
          <CheckoutProfileApiKey
            title="secret_api_key"
            value={selectedCheckoutProfile.secretProdKey}
            onChange={handleInputChange('secretProdKey')}
            error={errors.has('secretProdKey')}
          />
          {/* Do not remove hidden fields */}
          <CheckoutProfileApiKey
            title="secret_api_key_sandbox"
            hidden
            value={selectedCheckoutProfile.secretSandboxKey}
            onChange={handleInputChange('secretSandboxKey')}
          />
        </div>
        <div>
          <CheckoutProfileApiKey
            title="public_api_key"
            value={selectedCheckoutProfile.publicProdKey}
            onChange={handleInputChange('publicProdKey')}
            error={errors.has('publicProdKey')}
          />
          {/* Do not remove hidden fields */}
          <CheckoutProfileApiKey
            title="public_api_key_sandbox"
            hidden
            value={selectedCheckoutProfile.publicSandboxKey}
            onChange={handleInputChange('publicSandboxKey')}
          />
        </div>
      </div>

      <Button title={localeStore.t(BUTTON_TITLE.SAVE)} className="mt-8" onClick={handleSave} />
    </div>
  );
});

export default CheckoutProfileSettings;
